import { Component, OnInit, OnDestroy, ViewChild, ElementRef, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';

import { TableModule, Table, TablePageEvent } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { ConfirmationService, MessageService } from 'primeng/api';

import { Api } from '../../../api/api';
import {
  adminProjectsControllerListProjectsV1,
  AdminProjectsControllerListProjectsV1$Params,
  adminProjectsControllerDeleteProjectV1,
} from '../../../api/functions';
import { ProjectResponseAdminDto } from '../../../api/models/project-response-admin-dto';
import { HeaderService } from '../../../services/header.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil, skip } from 'rxjs';

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ConfirmDialogModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.css',
})
export class ProjectsComponent implements OnInit, OnDestroy {
  @ViewChild('table') table!: Table;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private readonly api = inject(Api);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);

  protected readonly projects = signal<ProjectResponseAdminDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly totalRecords = signal(0);
  protected readonly searchControl = new FormControl('');

  private readonly destroy$ = new Subject<void>();
  private isSearching = false;

  protected readonly queryParams = signal<AdminProjectsControllerListProjectsV1$Params>({
    page: 1,
    limit: 10,
  });

  protected first = 0;
  protected readonly rowsPerPageOptions = [10, 25, 50, 100];

  constructor() {}

  ngOnInit(): void {
    // Restore preferred page size from localStorage (if previously changed by the user)
    const savedLimit = Number(localStorage.getItem('admin.projects.limit'));
    if (!Number.isNaN(savedLimit) && savedLimit > 0) {
      const params = this.queryParams();
      this.queryParams.set({ ...params, limit: savedLimit });
    }

    this.headerService.setTitle(this.translateService.instant('projects.title'));
    this.headerService.setSubtitle(this.translateService.instant('projects.subtitle'));

    // Load initial data (onLazyLoad will be skipped for initial load)
    this.loadProjects();

    // Setup search with debounce
    this.searchControl.valueChanges
      .pipe(
        skip(1), // Skip initial value emission
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((searchTerm) => {
        // Mark that we're searching to restore focus later
        this.isSearching = true;

        // Reset to first page when searching
        this.first = 0;
        const params = this.queryParams();
        this.queryParams.set({
          ...params,
          page: 1,
          search: searchTerm || undefined,
        });
        this.loadProjects();
      });
  }

  protected onPageChange(event: TablePageEvent): void {
    const page = Math.floor((event.first || 0) / (event.rows || this.queryParams().limit!)) + 1;
    const limit = event.rows || this.queryParams().limit!;

    this.first = event.first || 0;

    // Persist user's page size preference
    const currentLimit = this.queryParams().limit!;
    if (limit !== currentLimit) {
      localStorage.setItem('admin.projects.limit', String(limit));
    }

    // Preserve search term when paginating
    const currentParams = this.queryParams();
    this.queryParams.set({
      page,
      limit,
      search: currentParams.search,
    });

    this.loadProjects();
  }

  private loadProjects(): void {
    this.loading.set(true);
    this.searchControl.disable();

    this.api
      .invoke(adminProjectsControllerListProjectsV1, this.queryParams())
      .then((response) => {
        this.projects.set(response.projects || []);
        this.totalRecords.set(response.total || 0);
      })
      .catch(() => {
        this.projects.set([]);
        this.totalRecords.set(0);
      })
      .finally(() => {
        this.loading.set(false);
        this.searchControl.enable();

        // Restore focus to search input if we were searching
        if (this.isSearching && this.searchInput) {
          setTimeout(() => {
            this.searchInput.nativeElement.focus();
            this.isSearching = false;
          }, 0);
        }
      });
  }

  /**
   * Get display status for a project.
   * If project is not published, show editorial status.
   * If project is published, show lifecycle status.
   */
  protected getDisplayStatus(project: ProjectResponseAdminDto): string {
    if (project.status !== 'published') {
      // Editorial status: draft, pending_approval, approved, rejected
      return project.status;
    }
    // Published project: show lifecycle status
    return project.lifecycleStatus || 'upcoming';
  }

  /**
   * Get status translation key based on display status.
   * Editorial statuses use 'projects.status.*'
   * Lifecycle statuses use 'projects.card.status.*'
   */
  protected getStatusTranslationKey(project: ProjectResponseAdminDto): string {
    const displayStatus = this.getDisplayStatus(project);
    if (project.status !== 'published') {
      return `projects.status.${displayStatus}`;
    }
    return `projects.card.status.${displayStatus}`;
  }

  protected getStatusSeverity(
    project: ProjectResponseAdminDto
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const displayStatus = this.getDisplayStatus(project);
    switch (displayStatus) {
      // Editorial statuses
      case 'approved':
        return 'info';
      case 'pending_approval':
        return 'warn';
      case 'rejected':
        return 'danger';
      case 'draft':
        return 'secondary';
      // Lifecycle statuses
      case 'upcoming':
        return 'warn';
      case 'privateSale':
        return 'info';
      case 'publicSale':
        return 'success';
      case 'claiming':
        return 'info';
      case 'funded':
        return 'contrast';
      case 'refunded':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  protected approveProject(project: ProjectResponseAdminDto): void {
    // Navigate to project edit page where approve action is available
    this.router.navigate(['/projects', project.id]);
  }

  protected rejectProject(project: ProjectResponseAdminDto): void {
    // Navigate to project edit page where reject action is available
    this.router.navigate(['/projects', project.id]);
  }

  protected editProject(project: ProjectResponseAdminDto): void {
    this.router.navigate(['/projects', project.id]);
  }

  protected addProject(): void {
    this.router.navigate(['/projects', 'new']);
  }

  protected refreshProjects(): void {
    const params = this.queryParams();
    this.queryParams.set({
      ...params,
      page: Math.floor(this.first / params.limit!) + 1,
    });
    this.loadProjects();
  }

  protected deleteProject(project: ProjectResponseAdminDto): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('projects.confirmDelete.message', {
        name: project.name,
      }),
      header: this.translateService.instant('projects.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptLabel: this.translateService.instant('projects.confirmDelete.confirm'),
      rejectLabel: this.translateService.instant('projects.confirmDelete.cancel'),
      accept: () => {
        this.api
          .invoke(adminProjectsControllerDeleteProjectV1, { id: project.id })
          .then(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('projects.success.deleteTitle'),
              detail: this.translateService.instant('projects.success.deleteMessage', {
                name: project.name,
              }),
              life: 4000,
            });
            this.refreshProjects();
          })
          .catch(() => {});
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
